import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import "./DepartmentMultiSelect.css";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeselectIcon from "@mui/icons-material/Deselect";
const DROPDOWN_MARGIN = 8; // small breathing room before the clipped edge
const normalize = (str) =>
  str
    .normalize("NFD") //separate caracters from accents
    .replace(/[\u0300-\u036f]/g, "") // remove accents/diacritics(ô → o)
    .toLowerCase();
const DepartmentMultiSelect = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Sélectionner des départements",
  disabled = false,
  isSmallScreen = false,
  fullAccessMode = false,
  maxCustom = 5,
  triggerTooltip,
  allowSelectAllControls = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(320);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current.focus(), 100);
    }
  }, [isOpen]);

  // Measure how much room is actually left inside the nearest clipping
  // ancestor (.drag-comp-body or .sidebar-draggableComp) and clamp the
  // dropdown to that, instead of always assuming a flat 320px.
  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) return;

    const clippingAncestor = dropdownRef.current.closest(
      ".drag-comp-body, .sidebar-draggableComp",
    );

    if (!clippingAncestor) {
      setDropdownMaxHeight(320);
      return;
    }

    const recalc = () => {
      const ancestorRect = clippingAncestor.getBoundingClientRect();
      const triggerRect = dropdownRef.current.getBoundingClientRect();
      const availableSpace =
        ancestorRect.bottom - triggerRect.bottom - DROPDOWN_MARGIN;

      // Never exceed the default 320px, but shrink to fit when there's
      // less room (with a small floor so it's never unusably tiny).
      setDropdownMaxHeight(Math.max(120, Math.min(320, availableSpace)));
    };

    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [isOpen]);

  const isAllSelected = selectedValues.length === options.length;

  const toggleAll = () => {
    if (isAllSelected) return;
    onChange(options.map((opt) => opt.value));
  };
  // Restricted-mode "select all" — respects the maxCustom parallel-selection cap
  const selectAllRestricted = () => {
    if (options.length > maxCustom) {
      triggerTooltip?.(
        `Vous ne pouvez sélectionner que ${maxCustom} départements en parallèle maximum.`,
      );
      return;
    }
    onChange(options.map((opt) => opt.value));
  };

  // Restricted-mode "deselect all" — keeps at least one selected, per the
  // existing "at least one department must remain selected" rule.
  const deselectAllRestricted = () => {
    if (selectedValues.length <= 1) return;
    onChange([selectedValues[0]]);
  };
  const toggleOption = (value) => {
    if (fullAccessMode) {
      if (isAllSelected) {
        onChange([value]);
        return;
      }
      if (selectedValues.includes(value)) {
        const next = selectedValues.filter((v) => v !== value);
        if (next.length === 0) {
          onChange(options.map((opt) => opt.value));
        } else {
          onChange(next);
        }
        return;
      }
      if (selectedValues.length >= maxCustom) {
        triggerTooltip?.(
          `Vous ne pouvez sélectionner que ${maxCustom} départements en parallèle maximum.`,
        );
        return;
      }
      onChange([...selectedValues, value]);
      return;
    }

    // Restricted mode: no "Tous" — just individual toggling
    if (selectedValues.includes(value)) {
      if (selectedValues.length === 1) return; // keep at least one
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      if (selectedValues.length >= maxCustom) {
        triggerTooltip?.(
          `Vous ne pouvez sélectionner que ${maxCustom} départements en parallèle maximum.`,
        );
        return;
      }
      onChange([...selectedValues, value]);
    }
  };

  const handleRemoveChip = (e, value) => {
    e.stopPropagation();
    toggleOption(value);
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleSearchClick = (e) => e.stopPropagation();

  const filteredOptions = options.filter((opt) =>
    normalize(opt.label).includes(normalize(searchTerm)),
  );

  const displayText = () => {
    if (selectedValues.length === 0) return placeholder;

    if (fullAccessMode) {
      if (isAllSelected) return "Tous les départements";
      const count = selectedValues.length;
      return `${count} département${count > 1 ? "s" : ""} sélectionné${count > 1 ? "s" : ""}`;
    }
    if (isAllSelected) return "Tous les départements";
    if (selectedValues.length <= 2) {
      return selectedValues
        .map((v) => options.find((o) => o.value === v)?.label)
        .filter(Boolean)
        .join(", ");
    }
    return `${selectedValues.length} départements sélectionnés`;
  };

  return (
    <div className="department-multi-select" ref={dropdownRef}>
      <div
        className={`multi-select-display ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="multi-select-text">{displayText()}</span>
        <span className="multi-select-arrow">{isOpen ? "▲" : "▼"}</span>
      </div>

      {isOpen && !disabled && (
        <div
          className="multi-select-dropdown"
          style={{ maxHeight: `${dropdownMaxHeight}px` }}
        >
          {fullAccessMode ? (
            <>
              <div
                className={`multi-select-option all-option ${isAllSelected ? "active" : ""}`}
                onClick={toggleAll}
              >
                <span
                  className={`radio-circle ${isAllSelected ? "checked" : ""}`}
                >
                  {isAllSelected && <span className="radio-dot" />}
                </span>
                <span className="all-option-label">Tous les départements</span>
              </div>

              {!isAllSelected && selectedValues.length > 0 && (
                <div className="multi-select-selected-area">
                  <div className="multi-select-selected-chips">
                    {selectedValues.map((v) => {
                      const label =
                        options.find((o) => o.value === v)?.label || v;
                      return (
                        <span
                          key={v}
                          className="multi-select-chip multi-select-chip-dropdown"
                        >
                          {label}
                          <button
                            type="button"
                            className="multi-select-chip-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveChip(e, v);
                            }}
                            disabled={disabled}
                            aria-label={`Retirer ${label}`}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="multi-select-search">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onClick={handleSearchClick}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>

              <div className="multi-select-options">
                {filteredOptions.length === 0 ? (
                  <div className="multi-select-no-results">
                    Aucun département trouvé
                  </div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected =
                      !isAllSelected && selectedValues.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        className={`multi-select-option ${isSelected ? "selected" : ""}`}
                        onClick={() => toggleOption(option.value)}
                      >
                        <span className="option-label">{option.label}</span>
                        {isSelected && (
                          <span className="option-check">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                            >
                              <path
                                d="M2 7L5.5 10.5L12 3.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              <div className="multi-select-search">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onClick={handleSearchClick}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                {allowSelectAllControls && (
                  <div className="multi-select-search-actions">
                    <button
                      type="button"
                      className="multi-select-icon-btn has-tooltip"
                      data-tooltip="Tout sélectionner"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAllRestricted();
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={isAllSelected}
                      aria-label="Tout sélectionner"
                    >
                      <SelectAllIcon sx={{ fontSize: 16 }} />
                    </button>

                    <button
                      type="button"
                      className="multi-select-icon-btn has-tooltip"
                      data-tooltip="Tout désélectionner (garde au moins un)"
                      onClick={(e) => {
                        e.stopPropagation();
                        deselectAllRestricted();
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={selectedValues.length <= 1}
                      aria-label="Tout désélectionner"
                    >
                      <DeselectIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                )}
              </div>

              <div className="multi-select-options">
                {filteredOptions.length === 0 ? (
                  <div className="multi-select-no-results">
                    Aucun département trouvé
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`multi-select-option ${
                        selectedValues.includes(option.value) ? "selected" : ""
                      }`}
                      onClick={() => toggleOption(option.value)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedValues.includes(option.value)}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span>{option.label}</span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
           <div className="multi-select-footer">
            <button
              type="button"
              className="multi-select-ok-btn"
              onClick={() => {
                setIsOpen(false);
                setSearchTerm("");
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentMultiSelect;
